/**
 * Kleed zoeken (fase 1: alleen bekijken)
 *
 * Waarom deze pagina bestaat: elke werklijst in deze app is een
 * status-gefilterde wachtrij. Heb je een kleed in handen dat niet in je lijst
 * staat, dan zie je nergens waarom. Hier zoek je op het nummer en zie je waar
 * het kleed nu staat, bij welke order het hoort en in welke lijst het valt.
 *
 * Bewust read-only: er wordt hier niets aangepast. Aanpassen gebeurt op het
 * station waar het kleed thuishoort.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { WbChip, WbFactRow, WbFlag, WbListRow, WbSearchField, WbState } from '../components/wb';
import { useHeader } from '../stores';
import { salesforceClient } from '../lib/salesforce';
import type { ZoekKleed } from '../lib/salesforce';

/** Minimaal aantal cijfers voordat we gaan zoeken (moet gelijk zijn aan de server). */
const MIN_CIJFERS = 3;
/** Wachttijd na de laatste toetsaanslag voordat we de server bevragen. */
const DEBOUNCE_MS = 400;

/** Waar de medewerker naartoe kan als het kleed wél in een lijst staat. */
const LIJST_ROUTE: Record<string, string> = {
  'voorinspectie': '/voorinspectie',
  'reinigen': '/reinigen',
  'na-inspectie': '/na-inspectie',
};

export function ZoekPage() {
  // ?q= wordt gevuld door de stationslijsten: zoekt een medewerker daar
  // tevergeefs, dan neemt die zoekterm hij mee hierheen in plaats van opnieuw
  // te moeten typen (en opnieuw een typefout te kunnen maken).
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get('q') ?? '');
  const [items, setItems] = useState<ZoekKleed[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gezocht, setGezocht] = useState(false);
  const [afgekapt, setAfgekapt] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Volgnummer per zoekopdracht: een traag antwoord op een oude zoekterm mag
  // een nieuwer resultaat nooit overschrijven.
  const volgnummer = useRef(0);

  const cijfers = query.replace(/\D/g, '');
  const teKort = cijfers.length > 0 && cijfers.length < MIN_CIJFERS;

  const zoek = useCallback(async (term: string) => {
    const mijn = ++volgnummer.current;
    setIsLoading(true);
    setError(null);
    try {
      const response = await salesforceClient.zoekKleden(term);
      if (mijn !== volgnummer.current) return;
      if (response.success && response.data) {
        setItems(response.data.items ?? []);
        setAfgekapt(response.data.afgekapt === true);
      } else {
        setItems([]);
        setError(response.message || 'Zoeken lukt nu niet. Probeer het zo opnieuw.');
      }
    } catch (err) {
      if (mijn !== volgnummer.current) return;
      setItems([]);
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'Zoeken lukt nu niet. Controleer je verbinding.'
      );
    } finally {
      if (mijn === volgnummer.current) {
        setIsLoading(false);
        setGezocht(true);
      }
    }
  }, []);

  // Debounce: pas zoeken als er even niet getypt is.
  useEffect(() => {
    if (cijfers.length < MIN_CIJFERS) {
      volgnummer.current++;
      setItems([]);
      setGezocht(false);
      setError(null);
      setIsLoading(false);
      return;
    }
    const timer = setTimeout(() => { void zoek(query); }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, cijfers.length, zoek]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useHeader('Kleed zoeken');

  return (
    <div className="space-y-4">
      <WbSearchField
        ref={inputRef}
        value={query}
        onChange={setQuery}
        placeholder="WI-nummer of WB-nummer"
        autoComplete="off"
      />
      <p className="text-sm text-wb-inkt2 -mt-2">Het nummer staat op het label van het kleed. Alleen de cijfers typen mag ook.</p>

      {teKort && <p className="text-sm text-wb-inkt2">Typ minstens {MIN_CIJFERS} cijfers.</p>}
      {isLoading && <WbState variant="laden" titel="Zoeken…" />}
      {!isLoading && error && <WbState variant="fout" titel={error} />}
      {!isLoading && !error && gezocht && items.length === 0 && (
        <WbState variant="leeg" titel="Niets gevonden." tekst={`Geen kleed of order met "${query}". Controleer het nummer op het label.`} />
      )}

      <div className="space-y-4">
        {!isLoading && items.map((item) => (
          <KleedKaart key={item.itemId} item={item} />
        ))}
        {afgekapt && (
          <p className="text-sm text-wb-inkt2 text-center">Meer treffers dan hier passen. Typ het volledige nummer.</p>
        )}
      </div>
    </div>
  );
}

/** Statussen met een lading: rood = staat stil of weg, groen = klaar voor de klant. */
function statusKind(status: string | null): 'actie' | 'klaar' | 'neutraal' {
  if (!status) return 'neutraal';
  if (['In Reparatie', 'In Reparatie EX', 'Niet afgeleverd', 'Geannuleerd'].includes(status)) return 'actie';
  if (['Ingepakt', 'Klaargelegd', 'In bus'].includes(status)) return 'klaar';
  return 'neutraal';
}

function KleedKaart({ item }: { item: ZoekKleed }) {
  const lijstRoute = item.lijst ? LIJST_ROUTE[item.lijst.key] : undefined;
  const kenmerken = [item.afmeting, item.vorm, item.soort, item.merk, item.productNaam].filter(Boolean).join(' · ');

  return (
    <section className="space-y-2">
      <WbListRow
        hoofd={item.itemNaam ?? 'Zonder nummer'}
        chips={
          <>
            <WbChip kind={statusKind(item.status)}>{item.status ?? 'Nog niet opgehaald'}</WbChip>
            {!item.isPrimair && <WbChip kind="wacht">Geen kleed</WbChip>}
          </>
        }
        sub={kenmerken || undefined}
      />
      <WbFactRow
        facts={[
          { label: 'Klant', value: item.werkOrder?.klantnaam ?? '–', tekst: true },
          { label: 'Order', value: item.werkOrder?.naam ?? '–' },
          {
            label: 'Lijst',
            tekst: true,
            children: item.lijst ? (
              lijstRoute ? (
                <Link to={lijstRoute} className="wb-focus text-base font-semibold underline underline-offset-4 text-wb-inkt truncate">{item.lijst.label}</Link>
              ) : (
                <p className="text-base font-semibold text-wb-inkt truncate">{item.lijst.label}</p>
              )
            ) : (
              <p className="text-base font-semibold text-wb-inkt2 truncate">Geen</p>
            ),
          },
        ]}
      />
      {item.werkOrder?.status && <p className="font-mono text-sm text-wb-inkt2 -mt-1">Order: {item.werkOrder.status}</p>}

      {/* De kernvraag: waarom staat dit kleed nergens */}
      <WbFlag kind="info">{item.uitleg}{!item.lijst ? ' Staat niet in een werklijst van de app.' : ''}</WbFlag>

      {item.waarschuwingen.map((w) => (
        <WbFlag key={w} kind="opmerking">{w}</WbFlag>
      ))}

      {item.omschrijving && <p className="text-sm text-wb-inkt2">{item.omschrijving}</p>}
      {item.wasrapportLaatste && <p className="text-xs text-wb-inkt2 italic">{item.wasrapportLaatste}</p>}
    </section>
  );
}
